import { useCallback, useMemo } from 'react';

import { OperationVariables } from '@apollo/client';
import { QueryResult, useLazyQuery } from '@apollo/client/react';
import { useGraphQLQuery } from '@betterment/graphql';

import { LoadingStatus } from './data';
import {
  GetPlanManagerSidebarDocument,
  GetPlanManagerSidebarQuery,
} from './graphql/query.getPlanManagerSidebar.generated';
import { GetPlanManagerSidebarEmployersWithTasksDocument } from './graphql/query.getPlanManagerSidebarEmployersWithTasks.generated';
import { createTaskCountsFromEmployers } from './helpers';

const getLoadingStatus = <T, U extends OperationVariables>(queryResult: QueryResult<T, U>): LoadingStatus => {
  if (queryResult.error) return 'error';
  if (queryResult.data !== undefined) return 'loaded';

  // Initial request is still loading
  return 'loading';
};

// For lazy queries, we need to account for the fact that they may not have been called yet
const getLazyLoadingStatus = (queryResult: { error?: unknown; loading: boolean; data?: unknown }): LoadingStatus => {
  if (queryResult.error != null) return 'error';
  if (queryResult.loading) return 'loading';
  if (queryResult.data !== undefined) return 'loaded';
  return 'idle'; // Not yet fetched
};

export const useSidebarDataQuery = (employerId: string) => {
  const query = useGraphQLQuery(GetPlanManagerSidebarDocument, {
    variables: { employerId },
  });

  const { data: rawData, refetch } = query;
  const data = rawData as GetPlanManagerSidebarQuery | undefined;

  const loadingStatus: LoadingStatus = getLoadingStatus(query);

  const employers = useMemo(() => {
    const unsortedEmployers = data?.employers.filter(Boolean) || [];
    return unsortedEmployers.slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const currentEmployer = data?.employer;
  const personalProfile = data?.personalProfile;

  const reload = useCallback(() => {
    void refetch();
  }, [refetch]);

  return {
    employers,
    currentEmployer,
    personalProfile,
    loadingStatus,
    reload,
  };
};

export const useEmployersWithTasksQuery = () => {
  const [loader, query] = useLazyQuery(GetPlanManagerSidebarEmployersWithTasksDocument);

  const { data } = query;

  const tasksLoadingStatus: LoadingStatus = getLazyLoadingStatus(query);

  const taskCounts: Record<string, number> = useMemo(
    () => createTaskCountsFromEmployers(data?.employers || []),
    [data]
  );

  const loadEmployersWithTasks = useCallback(() => {
    void loader();
  }, [loader]);

  return {
    taskCounts,
    tasksLoadingStatus,
    loadEmployersWithTasks,
  };
};
